import { debounce } from 'lodash';
import { ComponentPropsWithRef, useCallback, useMemo, useState } from 'react';

import { usePatientItems } from '../../../data/data/patients/use-patient-items';
import {
  GetPatientItemsQueryVariables,
  PatientGroup,
} from '../../../data/types/api/graphql';
import { useFormatters } from '../../../data/utils/formatters.utils';
import { InputAutoCompleteField } from '../../../elements/form/input/input-auto-complete-field';

type InputAutoCompletePatientFieldProps = Omit<
  ComponentPropsWithRef<typeof InputAutoCompleteField>,
  'options'
> & {
  optionVariables?: GetPatientItemsQueryVariables;
};

export function InputAutoCompletePatientField({
  optionVariables,
  ...props
}: InputAutoCompletePatientFieldProps) {
  const [search, setSearch] = useState<string>('');

  const { data: patientItemsData, loading: patientItemsDataLoading } =
    usePatientItems({
      ...(optionVariables ?? {}),
      group: optionVariables?.group ?? PatientGroup.All,
      search,
    });

  const patientItems = useMemo(
    () => patientItemsData?.items ?? [],
    [patientItemsData]
  );

  const onHandleSearch = useCallback(
    (value: string) => {
      setSearch(value);
    },
    [setSearch]
  );

  const onHandleSearchDebounce = useMemo(
    () => debounce(onHandleSearch, 250),
    [onHandleSearch]
  );

  const { getFullName } = useFormatters();

  return (
    <InputAutoCompleteField
      {...props}
      options={patientItems.map(option => ({
        value: option.id ?? '',
        title: getFullName(option),
      }))}
      loading={patientItemsDataLoading}
      onSearch={onHandleSearchDebounce}
    />
  );
}
